import type { Blok, Module } from './types'

/* ===========================================================================
 *  Voorlezen
 *
 *  Casper: "Eventueel met een stem erbij?"
 *
 *  De stem van het toestel, en geen geluidsbestand
 *  -----------------------------------------------
 *
 *  Een ingesproken opname zou beter klinken. Het zou ook betekenen: per module
 *  een bestand van een paar megabyte, opnieuw inspreken bij elke tekstwijziging,
 *  en een opname die stilletjes iets anders zegt dan er op het scherm staat
 *  zodra iemand één zin verbetert. Dat laatste is het ergste: dan is de cursus
 *  op twee plekken waar en dat kun je nergens aan zien.
 *
 *  Dit leest voor wat er STAAT. De tekst is de bron, dus hij kan niet uit de
 *  pas lopen, hij weegt niets, en hij werkt zonder verbinding.
 *
 *  Wat het kost: de stem is die van het toestel, en die klinkt op de ene
 *  tablet beter dan op de andere.
 *
 *  Geen Nederlandse stem? Dan geen knop
 *  ------------------------------------
 *
 *  Een Nederlandse tekst voorgelezen door een Engelse stem is onverstaanbaar
 *  en bovendien lachwekkend -- en dat laatste blijft hangen in plaats van de
 *  les. Liever geen knop dan een knop die iets onbruikbaars doet. Vandaar dat
 *  hieronder eerst gezocht wordt, en pas daarna getoond.
 * =========================================================================== */

/**
 * De stemmen komen niet meteen.
 *
 * In de meeste browsers is getVoices() de eerste keer leeg en komt de lijst
 * even later met een gebeurtenis binnen. Wie alleen de eerste aanroep doet,
 * concludeert dat er geen stemmen zijn -- en dat is bijna altijd onjuist.
 */
export function stemmenZoeken(klaar: (stem: SpeechSynthesisVoice | null) => void): () => void {
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
    klaar(null)
    return () => {}
  }

  let weg = false

  function kijk() {
    if (weg) return
    const stemmen = window.speechSynthesis.getVoices()
    if (stemmen.length === 0) return
    klaar(kiesNederlands(stemmen))
  }

  kijk()
  window.speechSynthesis.addEventListener('voiceschanged', kijk)

  /* Een vangnet. Sommige browsers sturen de gebeurtenis niet als de lijst al
     klaar stond voordat deze pagina er was. */
  const t = window.setTimeout(kijk, 600)

  return () => {
    weg = true
    window.clearTimeout(t)
    window.speechSynthesis.removeEventListener('voiceschanged', kijk)
  }
}

/**
 * De beste Nederlandse stem, of niets.
 *
 * Voorkeur voor een stem die op het toestel zelf staat (localService): die
 * werkt zonder verbinding en begint meteen. De online stemmen van Microsoft
 * klinken beter, maar een cursus die stilvalt zodra de wifi wegvalt is erger
 * dan een stem die wat blikkerig is.
 */
function kiesNederlands(stemmen: SpeechSynthesisVoice[]): SpeechSynthesisVoice | null {
  const nl = stemmen.filter((s) => s.lang.toLowerCase().startsWith('nl'))
  if (nl.length === 0) return null
  return nl.find((s) => s.localService) ?? nl[0] ?? null
}

/* ------------------------------------------------------------------ *
 *  Wat er voorgelezen wordt
 * ------------------------------------------------------------------ */

/**
 * De tekst van een module, in leesvolgorde.
 *
 * Uit de GEGEVENS en niet uit het scherm geplukt. Het scherm aflezen zou
 * betekenen dat er knoptekst, nummertjes en losse labels tussendoor komen --
 * "een", "twee", "nakijken", "volgende module". Zo komt eruit wat er te
 * vertellen valt, en verder niets.
 *
 * De werkvormen doen niet mee. Een sorteerspel voorlezen levert een opsomming
 * van veertien losse zinnen op zonder de vraag erbij, en dat is precies het
 * tegenovergestelde van wat die werkvorm doet.
 */
export function tekstVanModule(module: Module): string {
  const delen: string[] = [module.titel, module.kop]

  for (const blok of module.blokken) delen.push(...tekstVanBlok(blok))

  return delen
    .map((d) => d.trim())
    .filter(Boolean)
    .join('\n')
}

function tekstVanBlok(blok: Blok): string[] {
  switch (blok.soort) {
    case 'tekst':
      return blok.alineas
    case 'kern':
      return [blok.titel ?? '', ...blok.punten]
    case 'let-op':
      return ['Let op.', blok.tekst]
    case 'concept':
      return ['Nog niet vastgesteld.', blok.tekst]
    case 'vergelijk':
      return [blok.nietTitel, ...blok.niet, blok.welTitel, ...blok.wel]
    case 'tijdlijn':
      return blok.punten.flatMap((p) => [p.wanneer, p.wat, p.toelichting ?? ''])
    case 'stroom':
      return [
        blok.titel ?? '',
        ...blok.stappen.flatMap((s, i) => [
          `Stap ${i + 1}. ${s.naam}.`,
          s.wat,
          s.wie === 'machine' ? 'Dit doet de computer.' : 'Dit doe jij.',
        ]),
      ]
    case 'plaat':
      return [
        blok.onderschrift ?? '',
        ...(blok.wijzers ?? []).flatMap((w, i) => [`${i + 1}. ${w.naam}.`, w.wat]),
      ]
    case 'film':
      /* De ondertiteling IS de film; wie liever luistert dan kijkt, mist zo
         niets. De titel erbij, zodat duidelijk is waar het over gaat. */
      return [blok.titel, ...blok.scenes.map((s) => s.zeg)]
    default:
      /* sorteer, spot, keuze, prompt, kijk: zie de kop. */
      return []
  }
}

/* ------------------------------------------------------------------ *
 *  Het voorlezen zelf
 * ------------------------------------------------------------------ */

/**
 * In losse zinnen, en niet in één lange lap.
 *
 * Chrome kapt een uiting van meer dan ongeveer vijftien seconden halverwege af
 * -- een bekende eigenaardigheid die al jaren bestaat. Per zin opgeknipt
 * gebeurt dat niet, en het heeft een tweede voordeel: stoppen werkt meteen in
 * plaats van aan het eind van een alinea.
 */
export function lees(tekst: string, stem: SpeechSynthesisVoice, opKlaar: () => void): void {
  const synth = window.speechSynthesis
  synth.cancel()

  const zinnen = tekst
    .split(/(?<=[.!?:])\s+|\n+/)
    .map((z) => z.trim())
    .filter((z) => z.length > 0)

  if (zinnen.length === 0) { opKlaar(); return }

  zinnen.forEach((zin, i) => {
    const uiting = new SpeechSynthesisUtterance(zin)
    uiting.voice = stem
    uiting.lang = stem.lang
    /* Iets rustiger dan standaard. Dit is uitleg en geen nieuwsbericht. */
    uiting.rate = 0.95
    if (i === zinnen.length - 1) uiting.onend = opKlaar
    synth.speak(uiting)
  })
}

export function stop(): void {
  if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
    window.speechSynthesis.cancel()
  }
}
